import React, { Fragment, useEffect, useState } from "react";
import eventClientApi from "@/api-client/event-client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion";
import Avatar from "@/components/ui/avatar";
import Box from "@/components/ui/box";
import { Checkbox } from "@/components/ui/checkbox";
import { CollapsibleComponent } from "@/components/ui/collapse";
import { Input } from "@/components/ui/input";
import { Loader } from "@/components/ui/loader";
import Multiline from "@/components/ui/multiline";
import SectionHeader from "@/components/ui/section-header";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { cleanObject } from "@/lib/helper";
import { eventImageSizeMapper, getCurrentEpoc, getUserInitials } from "@/lib/utils";
import { inviteEmail } from "@/types/cusom-types";
import { ArrowDown2, ArrowLeft2, SearchNormal1 } from "iconsax-react";
import _ from "lodash";
import { Check, X } from "lucide-react";
import { useSession } from "next-auth/react";
import { motion } from "framer-motion";
import { ScrollArea } from "@/components/ui/scroll-area";
import NoDataView from "@/components/ui/no-data-view";
import useDebounce from "@/hooks/use-debounce";
import { useEventParticipations, usePastEvents } from "@/hooks/use-past-event-queries";
import useInView from "@/hooks/use-in-view";
import SkeletonListLoader from "../../manage-event/guest-skelaton";
import { Button } from "@/components/ui/button";

const prepareGetParticipantsURL = (data: {
  eventId: string;
  roles?: string;
  sortBy?: string[];
  status?: string;
  searchText?: string;
  details?: boolean;
  limit?: number;
  page?: number;
  searchParam?: string;
  searchValue?: string;
  checkin_epoc?: boolean;
}) => {
  return `api/v2/events/${data?.eventId}/event-participations?roles=${data.roles || ""}&statuses=${data.status || ""}&name=${encodeURIComponent(data.searchText || "")}&sort=${
    data.sortBy && data.sortBy.length ? data.sortBy.map((item) => `${item}:ASC`).join(",") : ""
  }${data.details === false ? "&details=false" : ""}${
    data.searchParam && data.searchValue
      ? "&searchParam=" + data.searchParam + "&searchValue=" + data.searchValue
      : ""
  }${typeof data.limit === "number" ? "&limit=" + data.limit : ""}${
    typeof data.page === "number" ? "&page=" + data.page : ""
  }${data.checkin_epoc === true ? "&checkin_epoc=true" : ""}`;
};

const PastEventGuest = ({
  event,
  eventId,
  BackToPreviousScreen,
  emails = [],
  setEmails,
  toggleUserSelection,
  checkIsUserPresent,
  checkIfAllSelect,
  toggleSelectAll
}: any) => {
  const [search, setSearch] = useState<string>("");
  const finalSearchValue = useDebounce(search, 300);
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: pastEventsLoading,
    totalRecords
  } = useEventParticipations({ eventId: eventId, search: finalSearchValue });

  const pastEventUsersList: any =
    data?.pages?.reduce((acc: any, current: any) => {
      return acc.concat(current.data);
    }, []) || [];

  const [users, setUsers] = useState<any>([]);

  const [usersListRef, isInView] = useInView({
    threshold: 0.1
  });

  useEffect(() => {
    if (isInView && hasNextPage && !isFetchingNextPage && !pastEventsLoading) {
      fetchNextPage();
    }
  }, [isInView, hasNextPage, isFetchingNextPage, pastEventsLoading, fetchNextPage]);

  return (
    <div className="bg-white p-ml z-20 h-full w-full absolute left-0 right-0 bottom-0 top-0">
      <div className="my-s">
        <Multiline
          className="bg-transparent px-0 !mx-0"
          icon={
            <div className="relative flex  items-center cursor-pointer">
              <ArrowLeft2
                className="mr-m cursor-pointer"
                onClick={() => {
                  BackToPreviousScreen();
                }}
              />
              <Avatar
                imgSrc={eventImageSizeMapper(event?.image_map)}
                size={56}
                activeRing={false}
                variant="image"
                present={false}
                presence={false}
                presenceState="blocked"
              />
            </div>
          }
          verticalAlign="center"
          titleText={
            <Text
              variant={"body_three_strong"}
              className="font-semibold transition-all duration-300 ease-in-out mb-0.5"
            >
              {event?.name}
            </Text>
          }
          text1={`Event | ${event?.totalInvited} People`}
          // text1={`Showing ${pastEventUsersList?.length || 0} / ${totalRecords} People`}
          titleTextSize={"medium"}
          text1Size={"body_one"}
          action={
            pastEventUsersList?.length > 0 && (
              <div className="flex items-center justify-center  gap-s mr-xs">
                <Button
                  variant="text"
                  onClick={() => {
                    // handleSelectAll(e);
                    toggleSelectAll(
                      "Event",
                      eventId,
                      [],
                      checkIfAllSelect("Event", eventId) ? false : true,
                      totalRecords
                    );
                  }}
                  className="ml-1"
                >
                  {checkIfAllSelect("Event", eventId) ? "Deselect all" : "Select all"}
                </Button>
              </div>
            )
          }
        />
      </div>
      <div className="border-b border-gray-200 w-full"></div>

      <div className="px-l mt-2xl">
        <Input
          variant="medium-s"
          className="rounded-xs w-full px-xl bg-[#F5F6F9]"
          inputClassName="w-full"
          placeholder="Search"
          value={search}
          onChange={(e) => {
            setUsers([]);
            setSearch(e.target.value);
          }}
          leftSectionBgClassName="bg-transparent !mr-2 items-center content-center p-none m-auto"
          leftSection={
            <SearchNormal1 size="24" className="text-color-secondary" variant="Outline" />
          }
          rightSectionClassName="py-none px-none pr-m-nudge"
          rightSection={
            search && (
              <X
                size={20}
                onClick={() => {
                  setUsers([]);

                  setSearch("");
                }}
              ></X>
            )
          }
        />
      </div>

      <div>
        {pastEventsLoading ? (
          <SkeletonListLoader />
        ) : (
          pastEventUsersList?.length > 0 && (
            <div>
              <ScrollArea
                className="h-[calc(100dvh-200px)]  flex flex-col mt-m"
                viewportClassName="h-[calc(100dvh-200px)]"
                type="auto"
              >
                <div className="px-xl">
                  {pastEventUsersList?.length ? (
                    <div>
                      {pastEventUsersList?.map((user: any, index: number) => {
                        if (!user) return;
                        const initals = getUserInitials(user);
                        const isSelected = checkIsUserPresent("Event", eventId, user?.user?.email);
                        return (
                          <div
                            onClick={() =>
                              toggleUserSelection(
                                "Event",
                                eventId,
                                user?.user?.email,
                                isSelected ? false : true
                              )
                            }
                            key={user?.id}
                            className="cursor-pointer"
                          >
                            <Multiline
                              key={user?.id}
                              className="!px-0"
                              icon={
                                <div className="flex gap-m items-center">
                                  {/* <Checkbox
                                  variant="rounded"
                                  // onCheckedChange={() => updateSelectedField(user, event?.id)}
                                  checked={isSelected}
                                /> */}
                                  <Avatar
                                    text={initals}
                                    imgSrc={
                                      user?.profile_optimized_image?.webp
                                        ? user?.profile_optimized_image?.webp
                                        : user?.profile_photo
                                    }
                                    size={56}
                                    activeRing={true}
                                    variant={user?.profile_photo ? "image" : "initials"}
                                    present={false}
                                    presence={false}
                                  />
                                </div>
                              }
                              text1={user?.user?.email}
                              titleTextSize={"medium"}
                              text1Size={"small"}
                              action={isSelected && <Check />}
                            />
                            <div className="border-b border-gray-200 w-full"></div>
                          </div>
                        );
                      })}
                    </div>
                  ) : pastEventsLoading ? (
                    <div className="w-full">
                      <Loader className="mx-auto" />
                    </div>
                  ) : null}
                  <div ref={usersListRef} className="h-5" />
                </div>
              </ScrollArea>
              <div className="w-full  mt-s absolute bottom-0   bg-white">
                <div className="flex gap-m justify-end  p-4">
                  <Button
                    variant={"primary"}
                    className="w-[150px]"
                    onClick={() => {
                      BackToPreviousScreen();
                    }}
                  >
                    Done
                  </Button>
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default PastEventGuest;
